import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from '@phosphor-icons/react';

import {
  DecisionIQHowOftenTextStyled,
  DecisionIQInfoBottomStyled,
  DecisionIQPrivacyContainerStyled,
} from '../../DecisionIQStyled';
import { Spacer } from 'theSource/components';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { DocFrequencySelect } from './DocFrequencySelect';
import { SubscriberFieldsDto } from 'theSource/types';

//CopilotBegin:

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1030;
`;
// Define the type for the props
type SlideUpModalProps = {
  isOpen: boolean;
};
//Add this for the modal animation
const SlideUpModal = styled.div<SlideUpModalProps>`
  @media (max-width: 743px) {
    position: fixed;
    bottom: 0;
    width: 100vw;
    left: 0;
    transform: translateY(${(props) => (props.isOpen ? '0' : '100%')});
    transition: transform 0.3s ease-in-out;
    z-index: 1031;
  }
`;

//Copilot End
export interface DecisionIQDoctorFrequencyProps {
  dependents?: DependentsResponseDto;
  subscriber?: SubscriberFieldsDto;
  orgBenefitId: number;
  showTitle?: boolean;
  subtitleText?: string;
}

export const DecisionIQDoctorFrequency = ({
  dependents,
  subscriber,
  orgBenefitId,
  showTitle = true,
  subtitleText,
}: DecisionIQDoctorFrequencyProps) => {
  const { t } = useTranslation('pages.decisionIQ');
  //Copilot Begin
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLinkClick = () => {
    setIsModalOpen(true);
  };
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseModal();
      }
    };
    document.addEventListener('keydown', (e) => keyListener(e));

    return () => document.removeEventListener('keydown', (e) => keyListener(e));
  });

  //Copilot End
  return (
    <div className="row" data-testid="decision-iq-doc-freq-page">
      <div className="col">
        <DecisionIQInfoBottomStyled>
          {showTitle && <h2 className="headline-3">{t('personalize_recs')}</h2>}
          <p className="mt-1 body1">{subtitleText || t('dive_in')}</p>
        </DecisionIQInfoBottomStyled>
        <Spacer height={4} />
        <div className="mx-n3_5 mx-md-n5 mx-lg-0">
          <div className="row align-items-baseline d-flex banner info border-0 rounded-0 rounded-media-lg mx-1 px-md-4 mx-lg-0">
            <div className="col-auto">
              <ShieldCheck size={32} color="#3C548E" weight="light" />
            </div>
            <div className="col px-2 pt-2_5">
              <h3 className="semiBold body1 mb-0">{t('your_privacy')}</h3>
              <p className="body2">
                {t('your_answers_are_used')} {t('this_information_will_not')}
              </p>
            </div>
          </div>
        </div>
        <Spacer height={8} />
        <p className="body1" id="frequencyText">
          {t('per_year_doctor_visit', {
            family:
              dependents && dependents.data.dependents.length > 0
                ? t('or_family')
                : '',
          })}
        </p>
        <button
          onClick={handleLinkClick}
          type="button"
          id="showCadence"
          className="btn btn-link"
        >
          {t('view_cadence')}
        </button>
        {isModalOpen && <Backdrop onClick={handleCloseModal} />}
        <SlideUpModal isOpen={isModalOpen} role="dialog" aria-modal="true">
          <DecisionIQPrivacyContainerStyled
            className="justify-content-between"
            id="cadenceBox"
          >
            <DecisionIQHowOftenTextStyled className="mb-3_5 mb-md-0 align-md-items-center justify-lg-content-start justify-md-content-center">
              <div className="semiBold body1 mb-0 pl-lg-2_5">
                {t('doctor_visit_frequency.often')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.often_m')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.often_f')}
              </div>
            </DecisionIQHowOftenTextStyled>
            <DecisionIQHowOftenTextStyled className="mb-3_5 mb-md-0 align-md-items-center justify-lg-content-start justify-md-content-center">
              <div className="semiBold body1 mb-0">
                {t('doctor_visit_frequency.average')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.avg_m')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.avg_f')}
              </div>
            </DecisionIQHowOftenTextStyled>
            <DecisionIQHowOftenTextStyled className="mb-3_5 mb-md-0 align-md-items-center justify-md-content-center ">
              <div className="semiBold body1 mb-0">
                {t('doctor_visit_frequency.rarely')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.rarely_m')}
              </div>
              <div className="ml-lg-2_5 body2">
                {t('doctor_visit_frequency.rarely_f')}
              </div>
            </DecisionIQHowOftenTextStyled>
          </DecisionIQPrivacyContainerStyled>
        </SlideUpModal>
        {orgBenefitId > 0 && <DocFrequencySelect
          dependents={dependents}
          subscriber={subscriber}
          orgBenefitId={orgBenefitId}
        />}
      </div>
    </div>
  );
};
